/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Grid } from '@mui/material';
import type { TFunction } from 'i18next';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { dateUtils } from '@opengeoweb/shared';
import ParameterDimensionIdSelect from './ParameterDimensionIdSelect';
import ParameterDimensionValueSelect from './ParameterDimensionValueSelect';
import { ReferenceTimeSelect } from './ParameterReferenceTimeSelect';
import { REFERENCE_TIME_ID } from '../../../constants';
import { getService } from '../../../store/selectors';
import type { TimeSeriesRootState } from '../../../store/types';
import {
  useGetDetailsForCollection,
  useGetInstances,
} from '../../../utils/timeseries-api/hooks';
import type { EDRInstance, Parameter } from '../../TimeSeries/types';

const ParameterDimensionSelect: React.FC<{
  rowIsEnabled: boolean;
  parameter: Parameter;
  patchParameter: (parameter: Partial<Parameter>) => void;
  t: TFunction;
}> = ({ rowIsEnabled, parameter, patchParameter, t }) => {
  const { serviceId, collectionId } = parameter;

  const service = useSelector((store: TimeSeriesRootState) =>
    getService(store, serviceId),
  );

  const [refTimeList, setRefTimeList] = useState<EDRInstance[]>([]);
  // Tracks which dimension id is currently being edited/shown, not persisted (mirrors DimensionSelect.tsx in libs/core)
  const [selectedDimensionId, setSelectedDimensionId] = useState<
    string | undefined
  >(undefined);
  const { data: collectionDetails } = useGetDetailsForCollection(
    service!,
    collectionId,
  );
  // Only do an instances call if instances are supported by the collection
  const supportsInstances = !!collectionDetails?.dataQueries?.instances;
  const { data: instances } = useGetInstances(
    service?.url!,
    collectionId,
    supportsInstances,
  );

  const customDimensions = collectionDetails?.customDimensions || [];

  React.useEffect(() => {
    if (instances) {
      // Sort the reference times in descending order (newest first)
      const sortedRefTimeList = instances
        .slice()
        .sort((instanceA, instanceB) => {
          return (
            dateUtils.parseCustomDateString(instanceB.id).getTime() -
            dateUtils.parseCustomDateString(instanceA.id).getTime()
          );
        });
      setRefTimeList(sortedRefTimeList);
    }
  }, [instances]);

  const hasReferenceTime =
    refTimeList.length > 0 &&
    !refTimeList.some((value) => value.id === 'unvalidated');

  // Show nothing if no reference time and neither any custom dimensions
  if (
    (!customDimensions || customDimensions.length === 0) &&
    !hasReferenceTime
  ) {
    return null;
  }

  return (
    <Grid container>
      <Grid size="grow">
        <ParameterDimensionIdSelect
          customDimensions={customDimensions}
          hasReferenceTime={hasReferenceTime}
          parameter={parameter}
          patchParameter={patchParameter}
          rowIsEnabled={rowIsEnabled}
          selectedDimensionId={selectedDimensionId}
          setSelectedDimensionId={setSelectedDimensionId}
          t={t}
        />
      </Grid>
      <Grid size="grow">
        {
          // Show reference time select if no dimension selected yet and reference time is available OR if reference time has been selected
          (!selectedDimensionId && hasReferenceTime) ||
          selectedDimensionId === REFERENCE_TIME_ID ? (
            <ReferenceTimeSelect
              parameter={parameter}
              patchParameter={patchParameter}
              refTimeList={refTimeList}
              rowIsEnabled={rowIsEnabled}
              t={t}
            />
          ) : (
            <ParameterDimensionValueSelect
              customDimensions={customDimensions}
              parameter={parameter}
              patchParameter={patchParameter}
              rowIsEnabled={rowIsEnabled}
              selectedDimensionId={selectedDimensionId}
              t={t}
            />
          )
        }
      </Grid>
    </Grid>
  );
};

export default ParameterDimensionSelect;
